import { Link } from "react-router-dom";
import { ArrowLeft, Home, Phone, Mail, MapPin, Globe, Clock } from "lucide-react";

export default function ComplaintsPolicy() {
  return (
    <div className="min-h-screen bg-[#020617] text-white">
      {/* Header */}
      <nav className="bg-[#0f172a] border-b border-slate-800 px-4 md:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-amber-500 rounded-lg flex items-center justify-center">
              <Home className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold">Thames Mortgage & Protection</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <div className="mb-8">
          <p className="text-blue-400 text-sm font-medium mb-2">OSL Financial Services</p>
          <h1 className="text-3xl font-bold mb-3" data-testid="complaints-heading">Complaint Handling Procedures</h1>
          <p className="text-slate-400">for OSL Financial Services Limited and its Appointed Representative firms.</p>
          <p className="text-slate-500 text-sm mt-2">If you would like this document in larger print or in another format, please contact us.</p>
        </div>
        
        <div className="prose prose-invert prose-slate max-w-none space-y-8 text-slate-300">

          {/* Your right to complain */}
          <section>
            <h2 className="text-xl font-semibold text-white">Your Right to Complain</h2>
            <p>
              We always aim to provide a high standard of service. If you are unhappy with any aspect of the service you have received, you have the right to complain. OSL Financial Services Ltd ("OSL") handles all complaints, including those relating to activities carried out by our Appointed Representatives ("ARs"). AR firms do not investigate complaints themselves.
            </p>
          </section>

          {/* How to contact us */}
          <section>
            <h2 className="text-xl font-semibold text-white">How to Contact Us</h2>
            <p>You can contact OSL directly using any of the following methods:</p>
            <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6 mt-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">By Phone</p>
                  <p className="text-white font-medium">01952 459683</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">By Post</p>
                  <p className="text-white font-medium">11 Dinthill, Telford, Shropshire, TF3 2DT</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">By Email</p>
                  <a href="mailto:info@osl-fs.co.uk" className="text-blue-400 hover:underline font-medium">info@osl-fs.co.uk</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Office Hours</p>
                  <p className="text-white font-medium">Monday to Friday, 9am to 5.30pm (except bank holidays)</p>
                </div>
              </div>
            </div>
          </section>

          {/* What we do when a complaint is received */}
          <section>
            <h2 className="text-xl font-semibold text-white">What We Do When a Complaint is Received</h2>
            <p>Our actions will be the same whether your complaint is made in writing or verbally. We aim to assess any complaint fairly, consistently and promptly.</p>
            <p>Where possible, we'll try to resolve your concerns within 3 working days of receiving your complaint.</p>

            <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6 mt-4">
              <h3 className="text-lg font-semibold text-blue-400 mb-3">If we can resolve your complaint within 3 working days, we will:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Contact you by the end of the third day of receiving your complaint, confirming how we have dealt with it.</li>
              </ul>
            </div>

            <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6 mt-4">
              <h3 className="text-lg font-semibold text-amber-400 mb-3">If we cannot resolve your complaint within 3 working days, we will:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Write to you within 5 working days of receiving your complaint.</li>
                <li>Investigate your complaint further.</li>
                <li>We may need to get a signed letter of authority from you so we can collect certain information from other parties on your behalf.</li>
                <li>We aim to complete our investigation within 8 weeks of receiving your complaint.</li>
                <li>Write to you again when we have completed our investigation, confirming:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Details of our investigation</li>
                    <li>Whether your complaint should be upheld or not</li>
                    <li>What corrective action or redress (or both) may be suitable</li>
                  </ul>
                </li>
                <li>If we haven't been able to complete our investigation within 8 weeks of receiving it, we will write to you again, explaining:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>The reason(s) why we have not been able to complete our investigation</li>
                    <li>When we hope to complete our investigation</li>
                  </ul>
                </li>
              </ul>
            </div>
          </section>

          {/* If you are unhappy with our response */}
          <section>
            <h2 className="text-xl font-semibold text-white">If You Are Unhappy with Our Response</h2>
            <p>
              If you're not happy with our response, you may refer your complaint to the Financial Ombudsman Service free of charge, who may be able to help. We'll provide full details of how to do this when we write to you.
            </p>
          </section>

          {/* What we do if your complaint is not about our firm */}
          <section>
            <h2 className="text-xl font-semibold text-white">What We Do If Your Complaint Is Not About Our Firm</h2>
            <p>If you complain to us, but we are satisfied that the complaint relates to another firm, we will:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Write to the firm concerned (if we can identify them), explaining that we believe they need to consider your complaint. We will also enclose details of your complaint. If this was made in writing — the copy of your original letter/email. If this was made verbally — written details of the notes we made.</li>
              <li>Write to you, giving you the firm's contact details. We will also provide you with a copy of the letter we send to the firm. A copy of the letter we send to you will also be sent to the firm concerned.</li>
            </ul>
          </section>

          {/* Financial Ombudsman Service */}
          <section>
            <h2 className="text-xl font-semibold text-white">Financial Ombudsman Service</h2>
            <p>Some, but not all, complaints can be referred to the Financial Ombudsman Service. You may be able to refer your complaint to FOS if:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The activity you are complaining about is regulated by the Financial Conduct Authority, and</li>
              <li>You are an eligible complainant (for example, a consumer, a micro-enterprise, a small business, a charity with turnover under £6.5m, or a trustee with assets under £5m)</li>
            </ul>
            <p>If your complaint qualifies, we will provide full details of how to contact FOS in our final response letter.</p>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mt-4">
              <p className="text-amber-400 font-semibold mb-2">Important</p>
              <p className="text-slate-300">
                If your complaint relates to unregulated activity such as commercial lending, limited company or SPV buy-to-let, or individual buy-to-let taken wholly for business purposes, or certain packaging-only services, the Financial Ombudsman Service may not be able to consider your complaint. We will make this clear in our final response.
              </p>
            </div>

            <p className="mt-4">The Financial Ombudsman Service are entirely independent, and the service is free. If your complaint is eligible for referral, the Financial Ombudsman Service can be contacted at:</p>

            <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6 mt-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Post</p>
                  <p className="text-white font-medium">The Financial Ombudsman Service, Exchange Tower, Harbour Exchange Square, London E14 9SR</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Phone</p>
                  <p className="text-white font-medium">0800 023 4567 or 0300 123 9123</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Email</p>
                  <a href="mailto:enquiries@financial-ombudsman.org.uk" className="text-blue-400 hover:underline font-medium">enquiries@financial-ombudsman.org.uk</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Website</p>
                  <a href="https://www.financial-ombudsman.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline font-medium">www.financial-ombudsman.org.uk</a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 px-4 md:px-8 mt-12">
        <div className="max-w-4xl mx-auto text-center text-slate-500 text-sm space-y-2">
          <p>OSL Financial Services Limited is authorised and regulated by the Financial Conduct Authority, FRN 948512.</p>
          <p>Registered office: 11 Dinthill, Telford, Shropshire, TF3 2DT. Registered in England and Wales. Registered No 06754978.</p>
          <p className="mt-4">© 2026 Thames Mortgage & Protection Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
